import type { Vec2 } from '../../geo/types';

/**
 * A lot source's centreline, measured once. The lot walk asks it for a point and a width some
 * hundred thousand times, and a pass from the start for each made them a fifth of lot planning.
 * It answers exactly as pointAtDistance and widthAtDistance do: bisection finds the segment
 * their passes stop at, whose running sums it keeps, and the arithmetic within it is theirs.
 */
export interface MeasuredLine {
  readonly length: number;
  pointAt(s: number): { readonly point: Vec2; readonly tangent: Vec2 };
  widthAt(s: number): number;
}

/** Running sums of segment lengths, as a pass adds them: ends[i] = starts[i] + lengths[i]. */
interface Sums {
  readonly starts: readonly number[];
  readonly lengths: readonly number[];
  readonly ends: readonly number[];
}

function sums(pts: readonly Vec2[], count: number, measure: (a: Vec2, b: Vec2) => number): Sums {
  const starts: number[] = [];
  const lengths: number[] = [];
  const ends: number[] = [];
  let run = 0;
  for (let i = 0; i < count; i++) {
    const length = measure(pts[i]!, pts[i + 1]!);
    starts.push(run);
    lengths.push(length);
    run += length;
    ends.push(run);
  }
  return { starts, lengths, ends };
}

/* pointAtDistance and polylineLength measure with a square root, widthAtDistance with hypot. */
function sqrtLength(a: Vec2, b: Vec2): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  return Math.sqrt(dx * dx + dy * dy);
}

function hypotLength(a: Vec2, b: Vec2): number {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

/** The first segment below `count` whose end reaches `s`, else `count`. */
function firstReaching(ends: readonly number[], s: number, count: number): number {
  let lo = 0;
  let hi = count;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (ends[mid]! >= s) hi = mid;
    else lo = mid + 1;
  }
  return lo;
}

function unit(dx: number, dy: number): Vec2 {
  const length = Math.sqrt(dx * dx + dy * dy);
  return length < 1e-10 ? [1, 0] : [dx / length, dy / length];
}

export function measureLine(centerline: readonly Vec2[], widths: readonly number[]): MeasuredLine {
  const segments = Math.max(0, centerline.length - 1);
  const along = sums(centerline, segments, sqrtLength);
  const widthSegments = Math.max(0, Math.min(centerline.length, widths.length) - 1);
  const across = sums(centerline, widthSegments, hypotLength);

  function pointAt(s: number): { readonly point: Vec2; readonly tangent: Vec2 } {
    if (segments === 0) return { point: centerline[0] ?? [0, 0], tangent: [1, 0] };
    /* The last segment takes any `s` beyond the end, as the pass stops there whatever the sum. */
    const i = firstReaching(along.ends, s, segments - 1);
    const a = centerline[i]!;
    const b = centerline[i + 1]!;
    const segLen = along.lengths[i]!;
    const t = segLen > 1e-10 ? Math.max(0, Math.min(1, (s - along.starts[i]!) / segLen)) : 0;
    return {
      point: [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])],
      tangent: unit(b[0] - a[0], b[1] - a[1]),
    };
  }

  function widthAt(s: number): number {
    if (widths.length === 0) return 0;
    const i = firstReaching(across.ends, s, widthSegments);
    if (i === widthSegments) return widths[widths.length - 1]!;
    const length = across.lengths[i]!;
    const t = length > 0 ? Math.max(0, (s - across.starts[i]!) / length) : 0;
    return widths[i]! + (widths[i + 1]! - widths[i]!) * t;
  }

  return { length: segments === 0 ? 0 : along.ends[segments - 1]!, pointAt, widthAt };
}
